/**
 * PitBrain Web — MUI Theme
 *
 * Builds MUI `Theme` objects from design tokens. Dark is the primary
 * experience; a light theme is provided for parity but not exposed in UI yet.
 *
 * Typography: Inter (owner decision, Epic #289, 2026-04-16).
 * Accent: #4fc2f7 (owner decision, Epic #289, 2026-04-16).
 */

import {
  createTheme,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles'
import {
  alpha,
  chartTokens,
  darkPalette,
  elevations,
  focusRing,
  fontFamily,
  fontSizes,
  lightPalette,
  motion,
  radius,
  spacing,
  type ChartTokens,
  type PaletteTokens,
  type ThemeMode,
} from './tokens'

/**
 * Module augmentation so consumers can read custom tokens off the theme
 * via `theme.tokens.*` without losing type safety. `palette` and `chart`
 * follow the active color mode — prefer them over importing palettes
 * directly so components render correctly in both modes.
 */
declare module '@mui/material/styles' {
  interface Theme {
    tokens: {
      mode: ThemeMode
      spacing: typeof spacing
      radius: typeof radius
      fontSizes: typeof fontSizes
      palette: PaletteTokens
      chart: ChartTokens
    }
  }
  interface ThemeOptions {
    tokens?: {
      mode: ThemeMode
      spacing: typeof spacing
      radius: typeof radius
      fontSizes: typeof fontSizes
      palette: PaletteTokens
      chart: ChartTokens
    }
  }
}

const buildThemeOptions = (
  mode: ThemeMode,
  tokens: PaletteTokens
): ThemeOptions => {
  // Primary CTAs carry the BRAND cyan, not the generic blue (which reads as
  // stock MUI). Contrast pairs per mode: bright cyan needs dark text
  // (10.2:1 on navy text), while light mode needs the deep cyan to clear
  // WCAG AA with white text (#0369A1 = 5.5:1; #0284C7 only reaches 4.0:1).
  const brandCta =
    mode === 'dark'
      ? { main: tokens.accentSolid, contrastText: darkPalette.appBackground }
      : { main: tokens.accentSecondary, contrastText: tokens.textOnAccent }

  return {
    palette: {
      mode,
      primary: brandCta,
      // The product blue steps down to the secondary tier.
      secondary: {
        main: tokens.primary,
        contrastText: tokens.textOnAccent,
      },
      error: { main: tokens.error },
      warning: { main: tokens.warning },
      success: { main: tokens.success },
      info: { main: tokens.accentSolid },
      background: {
        default: tokens.background,
        paper: mode === 'dark' ? tokens.modalCard : tokens.card,
      },
      text: {
        primary: tokens.text,
        secondary: tokens.textSecondary,
        disabled: tokens.disabled,
      },
      divider: tokens.border,
    },
    shape: {
      borderRadius: radius.medium,
    },
    typography: {
      fontFamily,
      fontSize: fontSizes.body,
      htmlFontSize: 16,
      caption: { fontSize: fontSizes.small, lineHeight: 1.4 },
      body2: { fontSize: fontSizes.small, lineHeight: 1.5 },
      body1: { fontSize: fontSizes.body, lineHeight: 1.6 },
      subtitle2: { fontSize: fontSizes.body, fontWeight: 600 },
      subtitle1: { fontSize: fontSizes.subtitle, fontWeight: 500 },
      h6: { fontSize: fontSizes.subtitle, fontWeight: 600 },
      h5: { fontSize: fontSizes.title, fontWeight: 600 },
      h4: { fontSize: fontSizes.heading, fontWeight: 600 },
      h3: { fontSize: 32, fontWeight: 700 },
      h2: { fontSize: 36, fontWeight: 700 },
      h1: {
        fontSize: fontSizes.display,
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: 0,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: tokens.background,
            color: tokens.text,
            fontFamily,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          // Token-driven keyboard focus ring, applied globally so every
          // interactive element gets consistent focus visibility.
          ':focus-visible': {
            outline: `${focusRing[mode].width}px solid ${focusRing[mode].color}`,
            outlineOffset: focusRing[mode].offset,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.small,
            paddingInline: spacing.md,
            paddingBlock: spacing.xs + 2,
          },
          // Outlined + Text buttons keep a neutral high-contrast foreground
          // and a subtle lighter border instead of their palette color,
          // matching the mobile app's "visible on dark card" feel (secondary's
          // #2563EB in particular is nearly invisible on dark backgrounds).
          outlined: {
            color: tokens.text,
            borderColor:
              mode === 'dark' ? 'rgba(255,255,255,0.24)' : tokens.border,
            '&:hover': {
              borderColor: tokens.accentSolid,
              backgroundColor: alpha(tokens.accentSolid, 0.08),
            },
          },
          text: {
            color: tokens.text,
            '&:hover': {
              backgroundColor:
                mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
          // color="inherit" buttons (e.g. the AppBar Login) must keep MUI's
          // `color: inherit` / `border-color: currentColor` so they pick up
          // the surface's contrast text — the neutral foreground above would
          // go dark on colored surfaces. MUI merges this slot after the
          // variant slots, so it wins for inherit buttons only.
          colorInherit: {
            color: 'inherit',
            borderColor: 'currentColor',
          },
          // Mobile destructive actions are not solid-red CTAs. They use a soft
          // red wash with red text (`DestructiveButton.tsx`), which reads as
          // dangerous without screaming over the dark UI. Washes derive from
          // the mode's error token; light mode washes are weaker because the
          // same alpha over white turns muddy pink.
          containedError: {
            backgroundColor: alpha(tokens.error, mode === 'dark' ? 0.2 : 0.12),
            color: tokens.error,
            '&:hover': {
              backgroundColor: alpha(
                tokens.error,
                mode === 'dark' ? 0.28 : 0.2
              ),
            },
          },
          outlinedError: {
            borderColor: alpha(tokens.error, 0.45),
            color: tokens.error,
            '&:hover': {
              borderColor: tokens.error,
              backgroundColor: alpha(tokens.error, 0.12),
            },
          },
          textError: {
            color: tokens.error,
            '&:hover': {
              backgroundColor: alpha(tokens.error, 0.08),
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: tokens.text,
            '&:hover': {
              backgroundColor:
                mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
          // Same inherit exemption as MuiButton: AppBar icon buttons must
          // pick up the bar's contrast text, not the forced neutral.
          colorInherit: {
            color: 'inherit',
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.medium,
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.card,
            color: tokens.textSecondary,
            textTransform: 'none',
            fontWeight: 500,
            gap: spacing.xs,
            minHeight: 44,
            paddingInline: spacing.md,
            paddingBlock: spacing.sm,
            transition: `background-color ${motion.duration.fast}ms ${motion.easing.standard}, border-color ${motion.duration.fast}ms ${motion.easing.standard}, color ${motion.duration.fast}ms ${motion.easing.standard}`,
            '& .MuiSvgIcon-root': {
              fontSize: 18,
            },
            '&:hover': {
              backgroundColor:
                mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
            },
            // Selected state follows the brand CTA pair so segmented controls
            // and primary buttons speak the same accent language.
            '&.Mui-selected': {
              backgroundColor: brandCta.main,
              color: brandCta.contrastText,
              borderColor: brandCta.main,
              '&:hover': {
                backgroundColor: brandCta.main,
              },
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            gap: spacing.sm,
            flexWrap: 'wrap',
            '& .MuiToggleButtonGroup-grouped': {
              borderRadius: `${radius.medium}px !important`,
              margin: '0 !important',
              border: `1px solid ${tokens.border}`,
            },
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
              borderLeft: `1px solid ${tokens.border}`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          // Per-mode elevation strategy: dark separates surfaces with a
          // border, light with a soft layered shadow (border softens so the
          // shadow does the separating).
          root: {
            backgroundColor: tokens.card,
            backgroundImage: 'none',
            borderRadius: radius.large,
            border: `1px solid ${
              mode === 'dark' ? tokens.border : alpha(tokens.text, 0.08)
            }`,
            boxShadow: mode === 'dark' ? 'none' : elevations.light.e1.boxShadow,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.small,
            backgroundColor: tokens.badgeBackground,
            color: tokens.badgeText,
            fontWeight: 500,
            fontSize: fontSizes.small,
            height: 26,
          },
          label: {
            // Explicit line-height + flex centering fixes the off-center text
            // caused by Inter's font metrics (default MUI uses 1.4375em which
            // sits slightly low with Inter on short chip labels).
            lineHeight: 1,
            paddingLeft: 10,
            paddingRight: 10,
            display: 'inline-flex',
            alignItems: 'center',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.small,
            // Subtle surface so inputs read as input affordances: a faint
            // white wash on the navy background in dark mode, a solid white
            // field on the gray page in light mode.
            backgroundColor:
              mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : tokens.card,
            transition: `background-color ${motion.duration.fast}ms ${motion.easing.standard}, border-color ${motion.duration.fast}ms ${motion.easing.standard}, box-shadow ${motion.duration.fast}ms ${motion.easing.standard}`,
            '&:hover': {
              backgroundColor:
                mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : tokens.card,
            },
            // Stronger idle border than the default token so the field is
            // discoverable without hover.
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor:
                mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : tokens.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor:
                mode === 'dark' ? 'rgba(255, 255, 255, 0.32)' : tokens.border,
            },
            // Focus uses accent cyan (not primary blue) — higher contrast on
            // the dark navy background and matches our interactive accent.
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: focusRing[mode].color,
              borderWidth: 1.5,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(focusRing[mode].color, 0.13)}`,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.error,
            },
          },
          input: {
            color: tokens.text,
            '&::placeholder': {
              color: tokens.textSecondary,
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tokens.textSecondary,
            '&.Mui-focused': {
              // Cyan focus label, not dark primary blue.
              color: tokens.accentSolid,
            },
            '&.Mui-error': {
              color: tokens.error,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: tokens.textSecondary,
            '&.Mui-error': {
              color: tokens.error,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderBottom: `1px solid ${tokens.border}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            borderRight: `1px solid ${tokens.border}`,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.modalCard,
            color: tokens.text,
            border: `1px solid ${tokens.border}`,
            fontSize: fontSizes.small,
          },
        },
      },
    },
    tokens: {
      mode,
      spacing,
      radius,
      fontSizes,
      palette: tokens,
      chart: chartTokens[mode],
    },
  }
}

export const pitbrainDarkTheme: Theme = createTheme(
  buildThemeOptions('dark', darkPalette)
)

export const pitbrainLightTheme: Theme = createTheme(
  buildThemeOptions('light', lightPalette)
)

export const pitbrainTheme = pitbrainDarkTheme
